import { FC } from "react"
import { RefreshCw, Trash2, ExternalLink } from "lucide-react"
import { EnrollmentResponse } from "../../enroll/types"

const MAX_RETRIES = 3
const STALE_SECONDS = 300

interface Props {
  enrollment: EnrollmentResponse
  isRetrying: boolean
  onRetry: () => void
  onDelete: () => void
}

const ActionBar: FC<Props> = ({
  enrollment,
  isRetrying,
  onRetry,
  onDelete
}) => {
  const { status, retry_count, updated_at } = enrollment
  const isStale = Date.now() / 1000 - updated_at > STALE_SECONDS
  const isFresh = status === "started" && !isStale

  // Retry visibility and state
  const showRetry = status !== "complete"
  const canRetry =
    (status === "started" && isStale) ||
    ((status === "partial" || status === "failed") && retry_count < MAX_RETRIES)
  const retryLabel =
    status === "started" && isStale
      ? "Resume stalled"
      : isRetrying
      ? "Retrying..."
      : "Retry"
  const retryDisabled = !canRetry || isRetrying || isFresh

  // Delete visibility
  const canDelete = !isFresh

  // Stripe link
  const stripeCustomerId =
    enrollment.steps.billing?.status === "success"
      ? (enrollment.steps.billing.details as any)?.stripe_customer_id
      : null

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {showRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retryDisabled}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={14} className={isRetrying ? "animate-spin" : ""} />
          {retryLabel}
        </button>
      )}

      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-2 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          <Trash2 size={14} />
          Delete enrollment
        </button>
      )}

      {stripeCustomerId && (
        <a
          href={`https://dashboard.stripe.com/customers/${stripeCustomerId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700"
        >
          <ExternalLink size={14} />
          Open in Stripe
        </a>
      )}

      {showRetry && retry_count > 0 && (
        <span className="text-xs font-mono text-neutral-400 ml-auto">
          {retry_count} of {MAX_RETRIES} retries used
        </span>
      )}
    </div>
  )
}

export default ActionBar
